import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  sectionVariant, 
  staggerContainer, 
  itemVariant,
  viewportOnce,
  viewportOnceNear,
  cardHover
} from "@/lib/motion";
import { resolveIcon } from "@/lib/iconResolver";

export interface ServicesCompactContent {
  eyebrow: string;
  headline: string;
  exploreLink: string;
  exploreLinkText: string;
  systems: {
    icon: string;
    label: string;
    problem: string;
    capabilities: string[];
    outcome: string;
  }[];
}

export const servicesCompactDefaults: ServicesCompactContent = {
  eyebrow: "Capabilities",
  headline: "Systems, not services.",
  exploreLink: "/services",
  exploreLinkText: "Explore all systems",
  systems: [
    { icon: "Search", label: "Acquire", problem: "Most acquisition fails on intent, not reach", capabilities: ["Paid Search", "Paid Social", "Programmatic"], outcome: "Lower CAC" },
    { icon: "MousePointer", label: "Convert", problem: "Conversion is a clarity problem, not a UX problem", capabilities: ["Landing Pages", "CRO", "Creative Testing"], outcome: "Higher conversion" },
    { icon: "Mail", label: "Retain", problem: "Wrong timing kills more than wrong messaging", capabilities: ["Email Marketing", "Lifecycle", "Loyalty"], outcome: "Greater LTV" },
    { icon: "BarChart3", label: "Scale", problem: "Scaling without infrastructure breaks everything", capabilities: ["Analytics", "Attribution", "Strategy"], outcome: "Sustainable growth" },
  ],
};

interface ServicesCompactProps {
  content?: ServicesCompactContent;
}

const ServicesCompact = ({ content: c = servicesCompactDefaults }: ServicesCompactProps) => {
  return (
    <section className="py-12 md:py-16 lg:py-20 border-t border-border">
      <div className="container-wide">
        <motion.div 
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8 md:mb-10 lg:mb-12"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={sectionVariant}
        >
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-px bg-primary" />
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">
                {c.eyebrow}
              </span>
            </div>
            <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight">
              {c.headline}
            </h2>
          </div>
          <Link 
            to={c.exploreLink}
            className="text-sm font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-2 group"
          >
            {c.exploreLinkText}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>

        <motion.div 
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-border"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnceNear}
          variants={staggerContainer}
        >
          {c.systems.map((system) => {
            const Icon = resolveIcon(system.icon);
            return (
              <motion.div
                key={system.label}
                variants={itemVariant}
                whileHover={cardHover}
              >
                <Link
                  to="/services"
                  className="group block bg-background p-5 md:p-6 lg:p-10 transition-colors duration-150 hover:bg-muted/20 h-full"
                >
                  <div className="w-10 h-10 rounded-lg bg-muted/50 flex items-center justify-center mb-6 transition-colors group-hover:bg-muted">
                    <Icon className="h-5 w-5 text-foreground/60" strokeWidth={1.5} />
                  </div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-display font-medium text-foreground">{system.label}</h3>
                    <ArrowRight className="h-4 w-4 text-muted-foreground/0 group-hover:text-primary transition-all" />
                  </div>
                  <p className="text-xs text-muted-foreground mb-4 leading-relaxed">{system.problem}</p>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {system.capabilities.map((cap) => (
                      <span key={cap} className="text-xs text-foreground/60 bg-muted/50 px-2 py-1 rounded">{cap}</span>
                    ))}
                  </div>
                  <span className="text-xs font-medium text-primary">{system.outcome}</span>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesCompact;
